import { Export, ImportCurve, Send2, Trash } from 'iconsax-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { useTypedParams } from 'react-router-typesafe-routes';
import MediaContent from '@/components/common/media-content/media-content';
import ProgressButton from '@/components/common/progress-button';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import useCollections from '@/hooks/collections/use-collections';
import useDeleteCollection from '@/hooks/collections/use-delete-collection';
import { useFriendBySessionId } from '@/hooks/contacts/use-friends';
import useBatchForward from '@/hooks/forward/use-batch-forward';
import useGroupInfo from '@/hooks/groups/use-group-info';
import useDownloadUrl from '@/hooks/use-download-url';
import { ConversationMessageContentType } from '@/models/conversations/conversation';
import type { ConversationType } from '@/models/conversations/conversation-session';
import ROUTES from '@/routes';
import { makePrompt } from '@/stores/prompter';
import { addToast } from '@/stores/toast';
import { getCollectionDownloadParams } from '@/utils/get-message-downloadable';
import cn from '@/utils/merge-class-names';
import { parseCollectionPreviewDate } from '@/utils/parseTime';
import { generateSessionKey } from '@/utils/session';
import '@cyntler/react-doc-viewer/dist/index.css';

interface Props {
  type?: ConversationType;
  id?: number;
}

export default function CollectionByIdRoute({ type: sessionType, id: sessionId }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { collectionId } = useTypedParams(ROUTES.MY.COLLECTIONS.BY_ID);
  const { collections } = useCollections();
  const { mutateAsync: deleteCollection } = useDeleteCollection();
  const { mutateAsyncWithPrompt: forwardCollections } = useBatchForward();
  const { handleDownloadUrl, isLoading: isDownloading } = useDownloadUrl();
  const { friend } = useFriendBySessionId(sessionType === 'session' ? sessionId : null);
  const { data: group } = useGroupInfo(sessionType === 'group' ? sessionId : null);

  const collection = collections.find((c) => c.ID === Number(collectionId));

  const canDownload = useMemo(
    () =>
      collection?.Type &&
      [
        ConversationMessageContentType.Image,
        ConversationMessageContentType.Video,
        ConversationMessageContentType.File,
      ].includes(collection.Type),
    [collection?.Type],
  );
  const shouldDarkMode = collection
    ? [ConversationMessageContentType.Image, ConversationMessageContentType.Video].includes(collection.Type)
    : false;

  const handleDownload = () => {
    if (!collection) return;

    const params = getCollectionDownloadParams(collection);
    if (!params) return;

    handleDownloadUrl(params.url, params.type, params.key).catch(console.warn);

    addToast({
      type: 'success',
      message: t('common.download-success'),
    });
  };

  const handleForward = async () => {
    if (!(sessionType && sessionId)) return;

    const key = generateSessionKey(sessionType, sessionId);
    if (
      await forwardCollections({
        collectionIds: [collectionId],
        targets: [
          sessionType === 'group'
            ? { key, type: 'group', name: group?.GroupData?.GroupName ?? '', groupId: sessionId }
            : {
                key,
                type: 'user',
                name: friend?.RemarkName || friend?.FriendNickName || '',
                userId: friend?.FriendID ?? 0,
              },
        ],
      })
    ) {
      navigate(-1);
    }
  };

  const handleDeleteCollection = async () => {
    if (
      collection &&
      (await makePrompt({
        key: 'delete-collection',
        title: t('my-collection.delete'),
        content: t('my-collection.confirm-delete'),
        promise: () => deleteCollection(collection.ID),
      }))
    ) {
      navigate(-1);
    }
  };

  const source =
    collection?.Type === ConversationMessageContentType.File
      ? `${collection.SmallPicturePath}|${collection.Content}`
      : (collection?.Content ?? '');

  return (
    <PageLayout
      headerConfig={{
        navigateBack: true,
        className: cn({ 'bg-neutral-950 text-neutral-0': shouldDarkMode }),
        headerLabel: collection && (
          <div className='flex flex-col text-sm size-full flex-center'>
            <span className='font-bold'>{collection.FromName}</span>
            <span>{parseCollectionPreviewDate(collection.TimeStamp)}</span>
          </div>
        ),
      }}
      title={t('my-collection.title')}
      bannerColor='#000'
      className={cn('flex flex-col gap-1', { 'bg-neutral-950 text-neutral-0': shouldDarkMode })}>
      {collection && (
        <div
          className={cn('flex flex-1 flex-col', {
            'mx-5':
              collection?.Type &&
              [ConversationMessageContentType.Text, ConversationMessageContentType.File].includes(collection.Type),
          })}>
          <MediaContent
            autoPlay
            isInView
            type={collection.Type}
            src={source}
          />

          <div className='py-6 flex items-center justify-around'>
            {sessionType ? (
              <button
                type='button'
                className='btn btn-ghost flex-nowrap flex-col gap-2'
                onClick={handleForward}>
                <Send2
                  variant='Outline'
                  size={24}
                />

                <span>{t('conversation.forward.send')}</span>
              </button>
            ) : (
              <>
                <button
                  type='button'
                  className='btn btn-ghost flex-nowrap flex-col gap-2'
                  onClick={handleDeleteCollection}>
                  <Trash
                    variant='Outline'
                    size={24}
                  />

                  <span>{t('conversation.delete.delete')}</span>
                </button>

                {canDownload && (
                  <ProgressButton
                    as='button'
                    className='btn btn-ghost flex-nowrap flex-col gap-2'
                    isLoading={isDownloading}
                    label='conversation.setting.group.qr-code.save-image'
                    onClick={handleDownload}
                    icon={
                      <ImportCurve
                        variant='Outline'
                        size={24}
                      />
                    }
                  />
                )}

                <Link
                  to={ROUTES.MY.COLLECTIONS.BY_ID.$.FORWARD.$path({ relative: true })}
                  className='btn btn-ghost flex-nowrap flex-col gap-2'>
                  <Export
                    variant='Outline'
                    size={24}
                  />

                  <span>{t('conversation.forward.forward')}</span>
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </PageLayout>
  );
}
